// vectorService.js - Cloudflare Vectorize Integration for Baba AI
// Replaces local ChromaDB with cloud-native vector database
// Works via Cloudflare Worker proxy

const WORKER_URL = 'https://brave-search-proxy.reza-dizadji.workers.dev';

class VectorService {
  /**
   * Embed an archived chat into Vectorize
   * Called automatically after chat is archived to GDrive
   * 
   * @param {Object} archiveData - The archived chat data
   * @param {string} archiveData.archiveId - Unique ID for this archive
   * @param {string} archiveData.title - Chat title
   * @param {Array} archiveData.messages - Array of messages
   * @returns {Promise<Object>} Embedding result
   */
  async embedArchive(archiveData) {
    const { archiveId, title, messages } = archiveData;

    // Chunk messages into conversation turns (user + assistant pairs)
    const chunks = this.chunkMessages(messages);

    console.log(`[Vector] Embedding archive: ${title}`);
    console.log(`[Vector] Created ${chunks.length} chunks`);

    try {
      const response = await fetch(`${WORKER_URL}/vector/embed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          archiveId,
          title,
          chunks,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Embedding failed');
      }

      const result = await response.json();
      console.log(`[Vector] ✅ Embedded ${result.chunksEmbedded} chunks`);
      return result;
    } catch (error) {
      console.error('[Vector] ❌ Embed error:', error);
      throw error;
    }
  }

  /**
   * Chunk messages into conversation turns
   * Each chunk = user message + assistant response
   * 
   * @param {Array} messages - Array of {role, content, timestamp}
   * @returns {Array} Chunks with content and metadata
   */
  chunkMessages(messages) {
    const chunks = [];
    let i = 0;

    while (i < messages.length) {
      const msg = messages[i];

      if (msg.role === 'user') {
        const userMsg = msg;
        let assistantMsg = null;

        // Find corresponding assistant response
        if (i + 1 < messages.length && messages[i + 1].role === 'assistant') {
          assistantMsg = messages[i + 1];
        }

        // Create chunk content
        let content = `User: ${userMsg.content || ''}\n\n`;
        if (assistantMsg) {
          content += `Baba: ${assistantMsg.content || ''}`;
        }

        chunks.push({
          content,
          metadata: {
            userTimestamp: userMsg.timestamp || '',
            assistantTimestamp: assistantMsg?.timestamp || '',
            hasFiles: !!(userMsg.files && userMsg.files.length > 0)
          }
        });

        i += assistantMsg ? 2 : 1;
      } else {
        i += 1;
      }
    }

    return chunks;
  }

  /**
   * Semantic search across all archived chats
   * 
   * @param {string} query - Search query
   * @param {number} maxResults - Number of results (default 10)
   * @param {Object} filter - Optional metadata filter
   * @returns {Promise<Array>} Search results
   */
  async search(query, maxResults = 10, filter = null) {
    console.log(`[Vector] 🔍 Searching: "${query}"`);

    try {
      const response = await fetch(`${WORKER_URL}/vector/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          topK: maxResults,
          filter
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Search failed');
      }

      const result = await response.json();
      console.log(`[Vector] ✅ Found ${result.resultsCount} results`);
      return result.results;
    } catch (error) {
      console.error('[Vector] ❌ Search error:', error);
      return [];
    }
  }

  /**
   * Get Vectorize index statistics
   * 
   * @returns {Promise<Object>} Index stats
   */
  async getStats() {
    try {
      const response = await fetch(`${WORKER_URL}/vector/stats`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Stats failed');
      }

      return await response.json();
    } catch (error) {
      console.error('[Vector] ❌ Stats error:', error);
      return {
        success: false,
        error: error.message,
        index: { vectorCount: 0 }
      };
    }
  }

  /**
   * Check if Vectorize service is healthy
   * 
   * @returns {Promise<Object>} Health status
   */
  async healthCheck() {
    try {
      const response = await fetch(`${WORKER_URL}/health`);

      if (!response.ok) {
        return { healthy: false, error: 'Service unavailable' };
      }

      const health = await response.json();
      return {
        healthy: health.allHealthy,
        services: health.services,
        timestamp: health.timestamp
      };
    } catch (error) {
      return { healthy: false, error: error.message };
    }
  }

  /**
   * Delete vectors for a specific archive
   * 
   * @param {Array} vectorIds - Array of vector IDs to delete
   * @returns {Promise<Object>} Delete result
   */
  async deleteVectors(vectorIds) {
    try {
      const response = await fetch(`${WORKER_URL}/vector/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vectorIds })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Delete failed');
      }

      return await response.json();
    } catch (error) {
      console.error('[Vector] ❌ Delete error:', error);
      throw error;
    }
  }
}

// Export singleton instance
const vectorService = new VectorService();
export default vectorService;
